import * as React from "react";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { cn } from "@/lib/utils";

export const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => (
  <RadioGroupPrimitive.Root ref={ref} className={cn("grid gap-2", className)} {...props} />
));
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

/**
 * A radio rendered as a selectable card rather than a dot.
 *
 * Size, colour and courier are all "pick exactly one from a small set", and a
 * card is both a bigger tap target and able to carry a price — which a dot
 * beside a label cannot.
 */
export const RadioCard = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <RadioGroupPrimitive.Item
    ref={ref}
    className={cn(
      "relative flex w-full cursor-pointer items-center gap-3 rounded-xl border border-ink/12 bg-white px-4 py-3 text-left transition-colors",
      "hover:border-ink/25",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/25",
      "data-[state=checked]:border-primary data-[state=checked]:bg-brand-tint",
      "disabled:cursor-not-allowed disabled:opacity-45 disabled:hover:border-ink/12",
      className,
    )}
    {...props}
  >
    {children}
  </RadioGroupPrimitive.Item>
));
RadioCard.displayName = "RadioCard";

/**
 * A radio rendered as a round colour swatch.
 *
 * Shows the uploaded swatch image when the option has one. Without one it
 * shows a neutral disc with the initial — deliberately NOT a guessed colour,
 * because a swatch that shows the wrong finish is worse than one that shows
 * none. The label is for screen readers; the visible name sits in the section
 * heading beside the picker.
 */
export const RadioSwatch = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item> & {
    label: string;
    image?: string;
  }
>(({ className, label, image, disabled, ...props }, ref) => (
  <RadioGroupPrimitive.Item
    ref={ref}
    disabled={disabled}
    className={cn(
      "relative size-11 shrink-0 overflow-hidden rounded-full bg-cream ring-offset-2 ring-offset-white transition-shadow",
      "hover:ring-2 hover:ring-ink/20",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50",
      "data-[state=checked]:ring-2 data-[state=checked]:ring-brand",
      "disabled:cursor-not-allowed disabled:opacity-45 disabled:hover:ring-0",
      className,
    )}
    {...props}
  >
    {image ? (
      <img src={image} alt="" className="size-full object-cover" />
    ) : (
      <span aria-hidden className="flex size-full items-center justify-center text-xs font-medium text-ink/60">
        {label.slice(0, 1).toUpperCase()}
      </span>
    )}
    {disabled && (
      // A diagonal strike: "not available", readable on any swatch colour.
      <span
        aria-hidden
        className="absolute inset-0 bg-[linear-gradient(135deg,transparent_46%,rgba(22,33,28,0.6)_46%,rgba(22,33,28,0.6)_54%,transparent_54%)]"
      />
    )}
    <span className="sr-only">{label}</span>
  </RadioGroupPrimitive.Item>
));
RadioSwatch.displayName = "RadioSwatch";
